import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAccessToken, useCurrentUser } from '@/features/auth/state/auth'
import api from '@/lib/api'

export type Verba = {
  id: string
  id_apuracao_competencia: string
  id_type_fiscal: string
  id_user?: string | null
  nome: string
  value: number
}

export type CreateVerbaInput = {
  id_apuracao_competencia: string
  nome: string
  value: number
}

export type UpdateVerbaInput = {
  id: string
  nome?: string
  value?: number
}

const verbasQueryKey = (idApuracaoCompetencia?: string) => ['verbas', idApuracaoCompetencia]

export function useFetchVerbasByApuracaoCompetencia(idApuracaoCompetencia?: string) {
  const user = useCurrentUser()
  const token = useAccessToken()

  return useQuery({
    queryKey: verbasQueryKey(idApuracaoCompetencia),
    queryFn: async () => {
      if (!user || !token) throw new Error('Usuário ou token ausente')
      if (!idApuracaoCompetencia) throw new Error('ID da apuração competência ausente')

      const response = await api.get<Verba[]>(`/verbas/apuracao-competencia/${idApuracaoCompetencia}`)
      return response.data
    },
    enabled: Boolean(user && token && idApuracaoCompetencia)
  })
}

export function useCreateVerba() {
  const queryClient = useQueryClient()
  const user = useCurrentUser()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async (body: CreateVerbaInput) => {
      if (!user || !token) throw new Error('Usuário ou token ausente')
      const response = await api.post<Verba>('/verbas', body)
      return response.data
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: verbasQueryKey(data.id_apuracao_competencia) })
    }
  })
}

export function useUpdateVerba() {
  const queryClient = useQueryClient()
  const user = useCurrentUser()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateVerbaInput) => {
      if (!user || !token) throw new Error('Usuário ou token ausente')
      const response = await api.put<Verba>(`/verbas/${id}`, body)
      return response.data
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: verbasQueryKey(data.id_apuracao_competencia) })
    }
  })
}

export function useDeleteVerba() {
  const queryClient = useQueryClient()
  const user = useCurrentUser()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async (verba: Pick<Verba, 'id' | 'id_apuracao_competencia'>) => {
      if (!user || !token) throw new Error('Usuário ou token ausente')
      const response = await api.delete<Verba>(`/verbas/${verba.id}`)
      return response.data
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: verbasQueryKey(data.id_apuracao_competencia) })
    }
  })
}
